/**
 * PDF Content Slide
 * Displays PDF documents with multiple fallback strategies:
 * 1. Google Docs Viewer (works everywhere including mobile)
 * 2. Direct iframe embed as secondary attempt (desktop browsers with PDF plugins)
 * 3. "Open / Download" link as final fallback
 *
 * Mobile browsers lack built-in PDF renderers, so a direct PDF iframe
 * either shows blank or never fires onLoad (infinite spinner).
 * Google Docs Viewer is used as the primary viewer to guarantee mobile support.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ContentSlideProps } from '../types';
import { stripHtml } from '../../../../utils/slides/stripHtml';

/**
 * Build a Google Docs Viewer URL that can render any publicly-accessible PDF.
 */
function googleDocsViewerUrl(pdfUrl: string): string {
  return `https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`;
}

/** Rough mobile detection (covers iOS Safari, Chrome/Firefox Android, etc.) */
function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
    navigator.userAgent
  );
}

/** How long to wait before declaring a PDF iframe "stuck" (e.g. mobile blank iframe). */
const LOAD_TIMEOUT_MS = 10_000;

export default function PdfSlide({ slide, className = '' }: ContentSlideProps) {
  const { content } = slide;

  // On mobile, skip straight to Google Viewer.
  const mobile = isMobileDevice();

  const [loading, setLoading] = useState(true);
  const [directFailed, setDirectFailed] = useState(mobile); // skip direct on mobile
  const [fallbackFailed, setFallbackFailed] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // When transitioning from direct → fallback, re-enable the spinner.
  useEffect(() => {
    if (directFailed && !fallbackFailed) {
      setLoading(true);
    }
  }, [directFailed, fallbackFailed]);

  // Safety timeout: if onLoad never fires, move to next strategy.
  useEffect(() => {
    if (!content?.pdfUrl || !loading) return;

    timeoutRef.current = setTimeout(() => {
      if (!loading) return;
      // Timeout expired while still loading → treat current strategy as failed.
      if (!directFailed) {
        setDirectFailed(true);           // try Google Viewer
      } else if (!fallbackFailed) {
        setLoading(false);
        setFallbackFailed(true);         // show open-link fallback
      }
    }, LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutRef.current);
  }, [content?.pdfUrl, loading, directFailed, fallbackFailed]);

  const handleDirectLoad = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setLoading(false);
  }, []);

  const handleDirectError = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setDirectFailed(true);
  }, []);

  const handleFallbackLoad = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setLoading(false);
  }, []);

  const handleFallbackError = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setLoading(false);
    setFallbackFailed(true);
  }, []);

  const showDirectEmbed = content?.pdfUrl && !directFailed;
  const showFallbackViewer = content?.pdfUrl && directFailed && !fallbackFailed;
  const showOpenLink = content?.pdfUrl && directFailed && fallbackFailed;

  return (
    <div className={`mb-8 max-w-3xl mx-auto ${className}`}>
      {content?.showTitle !== false && content?.title && (
        <h2 className="text-3xl font-bold mb-4 text-center">
          {stripHtml(content.title)}
        </h2>
      )}

      {/* Strategy 1: Direct iframe (desktop only) */}
      {showDirectEmbed && (
        <div className="relative bg-card rounded-2xl border-2 border-border overflow-hidden shadow-lg mb-4">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-muted">
              <div className="flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Loading PDF…</p>
              </div>
            </div>
          )}
          <iframe
            src={`${content.pdfUrl}#toolbar=1&navpanes=0&scrollbar=0`}
            width="100%"
            height="100%"
            title="PDF content"
            className="border-none w-full h-[50vh] sm:h-[60vh] min-h-[260px] sm:min-h-[360px] max-h-[75vh]"
            onLoad={handleDirectLoad}
            onError={handleDirectError}
          />
        </div>
      )}

      {/* Strategy 2: Google Docs Viewer (primary on mobile, fallback on desktop) */}
      {showFallbackViewer && (
        <div className="relative bg-card rounded-2xl border-2 border-border overflow-hidden shadow-lg mb-4">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-muted">
              <div className="flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Loading PDF…</p>
              </div>
            </div>
          )}
          <iframe
            src={googleDocsViewerUrl(content.pdfUrl)}
            width="100%"
            height="100%"
            title="PDF content (Google Docs Viewer)"
            className="border-none w-full h-[50vh] sm:h-[60vh] min-h-[260px] sm:min-h-[360px] max-h-[75vh]"
            onLoad={handleFallbackLoad}
            onError={handleFallbackError}
          />
        </div>
      )}

      {/* Strategy 3: Final fallback – open / download links */}
      {showOpenLink && (
        <div className="w-full h-[40vh] min-h-[200px] sm:min-h-[240px] rounded-2xl border-2 border-border shadow-lg bg-muted flex flex-col items-center justify-center gap-4 px-6 text-center mb-4">
          <div className="text-4xl">📄</div>
          <p className="text-lg font-semibold text-foreground">PDF could not be displayed</p>
          <p className="text-sm text-muted-foreground max-w-md">
            The PDF source blocked in-page viewing. You can open or download it directly.
          </p>
          <a
            href={content.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Open PDF in new tab ↗
          </a>
        </div>
      )}

      {content?.pdfUrl && (
        <div className="flex items-center justify-end gap-3">
          <a
            href={content.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          >
            Open PDF in new tab ↗
          </a>
          <a
            href={content.pdfUrl}
            download
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          >
            Download ⬇
          </a>
        </div>
      )}
    </div>
  );
}
